// The look and timing of a packet's walk across the map, shared by every source that draws one (CoreScope carried
// paths, Meshview route trees; the first-party ray uses the same burst numbers). Pure, no imports, so
// tests/pathFx.test.mjs can run it directly. Written for this app; nothing here is copied from another project.
//
// One packet = a head travelling hop to hop leaving a trail. At every hop it LANDS: a small solid burst (grows to
// BURST_PEAK_PX over BURST_GROW_MS), a half-opaque orb that swells to ORB_PEAK_PX and fades over ORB_MS, and a thin
// ring that ripples out and fades over RIPPLE_MS. The segment that brought it there fades over SEGMENT_FADE_MS.

export const FLIGHT_MS = 700; // per hop (matches lib/animation.ts)

// --- Landing effects -------------------------------------------------------------------------------------------
export const BURST_PEAK_PX = 6; // half of the old 12 px disc
export const BURST_GROW_MS = 200;
export const ORB_START_PX = 3;
export const ORB_PEAK_PX = 12;
export const ORB_OPACITY = 0.5;
export const ORB_MS = 800;
export const RIPPLE_START_PX = 4;
export const RIPPLE_PEAK_PX = 22;
export const RIPPLE_MS = 1000;
export const RIPPLE_OPACITY = 0.8;
export const SEGMENT_FADE_MS = 500; // after landing; was a 500 ms hold plus a 1600 ms fade
/** Everything an arrival leaves behind is gone this long after it lands (the ripple is the longest). */
export const LANDING_TAIL_MS = Math.max(RIPPLE_MS, ORB_MS, SEGMENT_FADE_MS);

// --- Head and line ---------------------------------------------------------------------------------------------
export const HEAD_PX = 3.5;
export const HEAD_GLOW_PX = 9;
export const HEAD_GLOW_OPACITY = 0.35;
export const LINE_GLOW_WIDTH_SCALE = 3.4; // the glow under the line is this much wider ...
export const LINE_GLOW_OPACITY = 0.22; // ... at this fraction of the line's own opacity
export const LINE_CORE_OPACITY = 0.85; // trail head-end opacity, up from 0.7
export const BRIDGE_OPACITY_SCALE = 0.45; // an unresolved stretch is drawn dim and dashed
export const BRIDGE_DASH: [number, number] = [2, 2];

export function easeOutCubic(t: number): number {
  const c = Math.min(1, Math.max(0, t));
  return 1 - Math.pow(1 - c, 3);
}

// --- Timing of a walk ------------------------------------------------------------------------------------------
export interface WalkState {
  phase: "travelling" | "fading";
  /** Ms since the traveller left, clamped to [0, totalTravelMs]. */
  elapsedIntoTravel: number;
  /** Opacity of the whole segment: 1 while travelling, then fading to 0 over SEGMENT_FADE_MS. */
  opacity: number;
}

/** One segment's state `elapsedMs` after its walk began; null before it leaves and once it has faded. */
export function walkState(startOffsetMs: number, totalTravelMs: number, elapsedMs: number): WalkState | null {
  const t = elapsedMs - startOffsetMs;
  if (t < 0) return null;
  if (t < totalTravelMs) return { phase: "travelling", elapsedIntoTravel: t, opacity: 1 };
  const since = t - totalTravelMs;
  if (since >= SEGMENT_FADE_MS) return null;
  return { phase: "fading", elapsedIntoTravel: totalTravelMs, opacity: 1 - since / SEGMENT_FADE_MS };
}

/** How long a walk stays worth keeping: until its last landing has finished. */
export function walkLifetimeMs(maxFinishMs: number): number {
  return maxFinishMs + LANDING_TAIL_MS;
}

export interface Landing {
  radiusPx: number;
  opacity: number;
}

/** The solid burst `ageMs` after landing: grows to BURST_PEAK_PX over BURST_GROW_MS, then fades with the segment. */
export function burstAt(ageMs: number, reduced = false): Landing | null {
  if (ageMs < 0 || ageMs >= SEGMENT_FADE_MS) return null;
  return {
    radiusPx: reduced ? BURST_PEAK_PX : BURST_PEAK_PX * (0.35 + 0.65 * easeOutCubic(ageMs / BURST_GROW_MS)),
    opacity: 1 - ageMs / SEGMENT_FADE_MS,
  };
}

/** The orb: half opaque, swells from ORB_START_PX to ORB_PEAK_PX and fades out over ORB_MS. None when reduced. */
export function orbAt(ageMs: number, reduced = false): Landing | null {
  if (reduced || ageMs < 0 || ageMs >= ORB_MS) return null;
  const t = ageMs / ORB_MS;
  return { radiusPx: ORB_START_PX + (ORB_PEAK_PX - ORB_START_PX) * easeOutCubic(t), opacity: ORB_OPACITY * (1 - t) };
}

/** The thin ring: ripples out and fades within RIPPLE_MS. None when reduced. */
export function rippleAt(ageMs: number, reduced = false): Landing | null {
  if (reduced || ageMs < 0 || ageMs >= RIPPLE_MS) return null;
  const t = ageMs / RIPPLE_MS;
  return { radiusPx: RIPPLE_START_PX + (RIPPLE_PEAK_PX - RIPPLE_START_PX) * easeOutCubic(t), opacity: RIPPLE_OPACITY * (1 - t) };
}

// --- Unresolved hops: bridging --------------------------------------------------------------------------------
export interface ChainHop {
  index: number; // hop_index on the wire
  pos: { lat: number; lon: number } | null; // null: not resolved to a placed node
}

export interface ChainEdge {
  from: { lat: number; lon: number };
  to: { lat: number; lon: number };
  fromIndex: number;
  toIndex: number;
  /** True when one or more unresolved hops lie between: drawn dim and dashed, so the chain stays continuous. */
  bridged: boolean;
}

/**
 * The edges of a packet's chain. Hops are taken in wire order; an edge joins each placed hop to the NEXT placed
 * hop. When the two are adjacent hop_indexes the edge is solid; when unresolved or missing hops lie between them it
 * is `bridged` (a dim dashed line from the last drawn hop to the next drawn hop). Drawing only: no hop is resolved
 * or invented, and an unresolved hop gets no landing. With no next placed hop the chain simply ends at the last one.
 */
export function chainEdges(hops: ChainHop[]): ChainEdge[] {
  const placed = [...hops].filter((h) => h.pos !== null).sort((a, b) => a.index - b.index);
  const edges: ChainEdge[] = [];
  for (let i = 0; i + 1 < placed.length; i++) {
    const a = placed[i];
    const b = placed[i + 1];
    edges.push({ from: a.pos!, to: b.pos!, fromIndex: a.index, toIndex: b.index, bridged: b.index !== a.index + 1 });
  }
  return edges;
}

/** Ms an edge takes to travel: one flight per hop it spans, so a bridged stretch is not faster than a real one. */
export function edgeTravelMs(e: Pick<ChainEdge, "fromIndex" | "toIndex">): number {
  return Math.max(1, e.toIndex - e.fromIndex) * FLIGHT_MS;
}

// --- Spreading a batch of CoreScope packets ------------------------------------------------------------------
export const SPREAD_MAX_WINDOW_MS = 60_000; // a batch is spread across at most this long
export const SPREAD_MIN_GAP_MS = 250;
export const SPREAD_MAX_GAP_MS = 2_400; // 25 packets (a full poll) across about 60 s

/**
 * The gap between packet starts for a batch of `n`: the batch window (the observed time between batches, never more
 * than SPREAD_MAX_WINDOW_MS, never less than n minimum gaps) divided by n, kept within the min and max gap. A first
 * batch with no history uses the full window.
 */
export function spreadGapMs(n: number, observedIntervalMs: number | null): number {
  if (n <= 0) return 0;
  const window = Math.min(SPREAD_MAX_WINDOW_MS, Math.max(n * SPREAD_MIN_GAP_MS, observedIntervalMs ?? SPREAD_MAX_WINDOW_MS));
  return Math.min(SPREAD_MAX_GAP_MS, Math.max(SPREAD_MIN_GAP_MS, window / n));
}

/**
 * Keeps track of when the next CoreScope packet may start so packets of a batch (and of the next batch, if the
 * previous one is still playing out) never start at once. `schedule(now, n)` returns n start delays in ms from
 * `now`; the first is 0 when nothing is queued. If the queue already runs past SPREAD_MAX_WINDOW_MS the gap shrinks
 * so the new batch still lands inside the window. Time is passed in, so this has no clock of its own.
 */
export class SpreadScheduler {
  private cursor = 0; // absolute time the next start is free
  private lastBatchAt: number | null = null;

  schedule(now: number, n: number): number[] {
    if (n <= 0) return [];
    const interval = this.lastBatchAt === null ? null : now - this.lastBatchAt;
    this.lastBatchAt = now;
    let gap = spreadGapMs(n, interval);
    const start = Math.max(now, this.cursor);
    const room = now + SPREAD_MAX_WINDOW_MS - start;
    if (n * gap > room) gap = Math.max(0, room / n);
    const out: number[] = [];
    for (let i = 0; i < n; i++) out.push(start + i * gap - now);
    this.cursor = start + n * gap;
    return out;
  }
}

// --- Colour -----------------------------------------------------------------------------------------------------
/** "#rrggbb" mixed `amount` (0 to 1) toward white, for the brighter head dot. Anything else is returned as is. */
export function brighten(hex: string, amount: number): string {
  const m = /^#([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const mix = (v: number) => Math.round(v + (255 - v) * Math.min(1, Math.max(0, amount)));
  return "#" + [(n >> 16) & 255, (n >> 8) & 255, n & 255].map((v) => mix(v).toString(16).padStart(2, "0")).join("");
}

/** True when the viewer asked for less motion (the walk then draws as static highlights). */
export function prefersReducedMotion(): boolean {
  const g = globalThis as { matchMedia?: (query: string) => { matches: boolean } };
  return typeof g.matchMedia === "function" && g.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// --- GeoJSON features the map layers draw ----------------------------------------------------------------------
export type FxFeature = { type: "Feature"; geometry: { type: "Point"; coordinates: [number, number] } | { type: "LineString"; coordinates: [number, number][] }; properties: Record<string, number | string> };

const point = (lon: number, lat: number, properties: FxFeature["properties"]): FxFeature => ({ type: "Feature", geometry: { type: "Point", coordinates: [lon, lat] }, properties });

/**
 * Everything a landing at (lon, lat) draws `ageMs` after it landed: the burst, the orb and the ripple, as Point
 * features the map's burst / orb / ripple layers pick up by `kind`. `colour` is the packet type's colour.
 */
export function landingFeatures(lon: number, lat: number, ageMs: number, colour: string, reduced: boolean): FxFeature[] {
  const out: FxFeature[] = [];
  const b = burstAt(ageMs, reduced);
  if (b) out.push(point(lon, lat, { kind: "burst", radiusPx: b.radiusPx, opacity: b.opacity, color: colour }));
  const o = orbAt(ageMs, reduced);
  if (o) out.push(point(lon, lat, { kind: "orb", radiusPx: o.radiusPx, opacity: o.opacity, color: colour }));
  const r = rippleAt(ageMs, reduced);
  if (r) out.push(point(lon, lat, { kind: "ripple", radiusPx: r.radiusPx, opacity: r.opacity, color: colour }));
  return out;
}

/** The travelling head: a brighter dot over a soft glow. */
export function headFeatures(lon: number, lat: number, colour: string): FxFeature[] {
  return [
    point(lon, lat, { kind: "headglow", radiusPx: HEAD_GLOW_PX, opacity: HEAD_GLOW_OPACITY, color: colour }),
    point(lon, lat, { kind: "head", radiusPx: HEAD_PX, opacity: 1, color: brighten(colour, 0.55) }),
  ];
}

/** One line piece. `bridged` pieces go to the dashed layer. */
export function lineFeature(a: { lon: number; lat: number }, b: { lon: number; lat: number }, opacity: number, widthScale: number, colour: string, bridged: boolean): FxFeature {
  return {
    type: "Feature",
    geometry: { type: "LineString", coordinates: [[a.lon, a.lat], [b.lon, b.lat]] },
    properties: { kind: bridged ? "bridge" : "seg", opacity: bridged ? opacity * BRIDGE_OPACITY_SCALE : opacity, widthScale, color: colour },
  };
}

/** CoreScope's numeric MeshCore payload type as the wire name the packet-type colours are keyed on. */
export const MESHCORE_PAYLOAD_TYPE_NAME: Record<number, string> = {
  0: "REQ", 1: "RESPONSE", 2: "TXT_MSG", 3: "ACK", 4: "ADVERT", 5: "GRP_TXT", 6: "GRP_DATA",
  7: "ANON_REQ", 8: "PATH", 9: "TRACE", 10: "MULTIPART", 11: "CONTROL",
};

export function meshcorePayloadTypeName(n: number | null | undefined): string | null {
  return n == null ? null : MESHCORE_PAYLOAD_TYPE_NAME[n] ?? null;
}
